import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Form } from '@/components/ui/form';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { Navbar } from '@/components/homepage/Navbar';
import Footer from '@/components/homepage/Footer';
import { scrollToFirstError } from '@/utils/formUtils';
import MultiStepSignUp from '@/components/backoffice/auth/MultiStepSignUp';
import { supabase } from '@/integrations/supabase/client';
import { getReferralCode, extractReferralFromUrl } from '@/utils/referralUtils';
import { enhancedInputValidator } from '@/utils/enhancedInputValidator';
import { translateSupabaseError } from '@/utils/supabaseErrorTranslator';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { getLocalizedPath } from '@/utils/languageUtils';

// Password validation helpers
const hasUpperCase = (str: string) => /[A-Z]/.test(str);
const hasLowerCase = (str: string) => /[a-z]/.test(str);
const hasNumber = (str: string) => /[0-9]/.test(str);
const hasSpecialChar = (str: string) => /[^a-zA-Z0-9]/.test(str);

// Calculate minimum date (18 years ago)
const isAtLeast18YearsOld = (dateString: string) => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  
  return date <= eighteenYearsAgo;
};

const SignUp = () => {
  const { signUp, isAuthenticated, loading, userRole } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const navigate = useLocalizedNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sponsorValid, setSponsorValid] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  
  const formRef = useRef<HTMLDivElement>(null);
  const pageTopRef = useRef<HTMLDivElement>(null);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    console.log(`[SignUp] Auth state: loading=${loading}, isAuthenticated=${isAuthenticated}, userRole=${userRole}, language=${language}`);
    if (!loading && isAuthenticated && userRole) {
      const basePath = userRole === 'admin' ? '/admin-dashboard' : '/dashboard';
      console.log(`[SignUp] Redirecting to: ${basePath}`);
      navigate(basePath, { replace: true });
    }
  }, [isAuthenticated, navigate, loading, userRole, language]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-validate sponsor if pre-populated — URL code takes priority over localStorage
  useEffect(() => {
    const urlCode = extractReferralFromUrl();
    const storedCode = getReferralCode();
    const initialSponsor = urlCode || storedCode || '';

    if (initialSponsor.length >= 3) {
      // If URL has a code that differs from what the form currently shows, update the field
      if (urlCode && urlCode !== form.getValues('sponsor')) {
        form.setValue('sponsor', urlCode);
      }

      supabase.rpc('is_referral_code_valid', { referral_code: initialSponsor })
        .then(({ data }) => {
          if (data) setSponsorValid(true);
        });
    }
  }, []);

  const signUpSchema = z.object({
    username: z.string()
      .min(3, { message: t('auth.usernameLength') })
      .max(20, { message: t('auth.usernameLength') })
      .transform((val) => enhancedInputValidator.sanitizeText(val, 20))
      .refine((val) => {
        const validation = enhancedInputValidator.validateUsername(val);
        return validation.isValid;
      }, { message: t('auth.usernameFormat') }),
    first_name: z.string()
      .min(1, { message: t('auth.mandatoryField') })
      .max(50, { message: t('auth.nameMaxLength') })
      .refine((val) => {
        const validation = enhancedInputValidator.validateName(val.trim(), 'First name');
        return validation.isValid;
      }, { message: t('auth.nameFormat') })
      .transform((val) => {
        const validation = enhancedInputValidator.validateName(val.trim(), 'First name');
        return validation.sanitizedValue || val.trim();
      }),
    last_name: z.string()
      .min(1, { message: t('auth.mandatoryField') })
      .max(50, { message: t('auth.nameMaxLength') })
      .refine((val) => {
        const validation = enhancedInputValidator.validateName(val.trim(), 'Last name');
        return validation.isValid;
      }, { message: t('auth.nameFormat') })
      .transform((val) => {
        const validation = enhancedInputValidator.validateName(val.trim(), 'Last name');
        return validation.sanitizedValue || val.trim();
      }),
    email: z.string()
      .email({ message: t('auth.invalidEmail') })
      .max(254, { message: t('auth.emailMaxLength') })
      .transform((val) => enhancedInputValidator.sanitizeText(val, 254))
      .refine((val) => {
        const validation = enhancedInputValidator.validateEmail(val);
        return validation.isValid;
      }, { message: t('auth.invalidEmail') }),
    confirm_email: z.string()
      .email({ message: t('auth.invalidEmail') })
      .max(254, { message: t('auth.emailMaxLength') }),
    date_of_birth: z.string()
      .min(1, { message: t('auth.mandatoryField') })
      .refine(isAtLeast18YearsOld, { message: t('auth.minimumAge') }),
    gender: z.string().min(1, { message: t('auth.mandatoryField') }),
    country: z.string().min(1, { message: t('auth.mandatoryField') }),
    country_code: z.string().min(1, { message: t('auth.mandatoryField') }),
    phone: z.string()
      .min(7, { message: t('auth.phoneMinLength') })
      .max(15, { message: t('auth.phoneMaxLength') })
      .transform((val) => val.replace(/\D/g, ''))
      .refine((val) => {
        const validation = enhancedInputValidator.validatePhone(val, '+1');
        return validation.isValid;
      }, { message: t('auth.phoneFormat') }),
    password: z.string()
      .min(8, { message: t('auth.passwordLength') })
      .max(128, { message: t('auth.passwordMaxLength') })
      .refine(hasUpperCase, { message: t('auth.passwordUppercase') })
      .refine(hasLowerCase, { message: t('auth.passwordLowercase') })
      .refine(hasNumber, { message: t('auth.passwordNumber') })
      .refine(hasSpecialChar, { message: t('auth.passwordSpecial') })
      .refine((val) => {
        const validation = enhancedInputValidator.validatePasswordForAuthentication(val, 128);
        return validation.isValid;
      }, { message: t('auth.commonPassword') }),
    confirm_password: z.string(),
    sponsor: z.string()
      .min(3, { message: t('auth.sponsorRequired') })
      .max(20, { message: t('auth.usernameLength') })
      .transform((val) => enhancedInputValidator.sanitizeText(val, 20))
      .refine((val) => {
        const validation = enhancedInputValidator.validateUsername(val);
        return validation.isValid;
      }, { message: t('auth.usernameFormat') }),
    terms: z.boolean().refine(val => val === true, {
      message: t('auth.termsRequired'),
    }),
  }).refine(data => data.email === data.confirm_email, {
    message: t('auth.emailsDoNotMatch'),
    path: ['confirm_email'],
  }).refine(data => data.password === data.confirm_password, {
    message: t('auth.passwordsDoNotMatch'),
    path: ['confirm_password'],
  });

  type SignUpFormValues = z.infer<typeof signUpSchema>;

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      confirm_email: '',
      date_of_birth: '',
      gender: '',
      country: '',
      country_code: '',
      phone: '',
      password: '',
      confirm_password: '',
      sponsor: extractReferralFromUrl() || getReferralCode() || '',
      terms: false,
    },
  });

  // Watch fields for real-time requirements feedback
  const passwordValue = form.watch('password');
  const sponsorValue = form.watch('sponsor');
  const usernameValue = form.watch('username');
  const emailValue = form.watch('email');

  // Debounced username uniqueness check for mobile (where onBlur may not fire)
  useEffect(() => {
    if (!usernameValue || usernameValue.length < 3) return;

    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('is_username_unique', { username: usernameValue });
        if (error) {
          console.error('Error checking username (debounced):', error);
          return;
        }
        if (!data) {
          form.setError('username', { type: 'manual', message: t('auth.usernameTaken') });
        }
      } catch (err) {
        console.error('Error checking username (debounced):', err);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [usernameValue]);

  // Debounced email uniqueness check for mobile (where onBlur may not fire)
  useEffect(() => {
    if (!emailValue || !emailValue.includes('@')) return;

    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('is_email_unique', { email: emailValue });
        if (error) {
          console.error('Error checking email (debounced):', error);
          return;
        }
        if (!data) {
          form.setError('email', { type: 'manual', message: t('auth.emailTaken') });
        }
      } catch (err) {
        console.error('Error checking email (debounced):', err);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [emailValue]);

  useEffect(() => {
    if (!sponsorValue || sponsorValue.length < 3) {
      setSponsorValid(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('is_referral_code_valid', {
          referral_code: sponsorValue,
        });
        if (error) {
          console.error('Error checking sponsor (debounced):', error);
          setSponsorValid(false);
          return;
        }
        if (data) {
          setSponsorValid(true);
          form.clearErrors('sponsor');
        } else {
          setSponsorValid(false);
          form.setError('sponsor', { type: 'manual', message: t('auth.sponsorNotFound') });
        }
      } catch (err) {
        console.error('Error checking sponsor (debounced):', err);
        setSponsorValid(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [sponsorValue]);
  
  useEffect(() => {
    setPasswordRequirements({
      length: passwordValue.length >= 8,
      uppercase: hasUpperCase(passwordValue),
      lowercase: hasLowerCase(passwordValue),
      number: hasNumber(passwordValue),
      special: hasSpecialChar(passwordValue)
    });
  }, [passwordValue]);

  // Check for form errors and scroll to the first one
  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0) {
      setTimeout(() => scrollToFirstError(formRef.current), 100);
    }
  }, [form.formState.errors]);

  const handleUsernameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const username = e.target.value;
    
    if (username.length < 3) {
      return;
    }
    
    try {
      const { data, error } = await supabase.rpc(
        'is_username_unique',
        { username }
      );
      
      if (error) {
        console.error('Error checking username:', error);
        return;
      }
      
      if (!data) {
        form.setError('username', { 
          type: 'manual', 
          message: t('auth.usernameTaken') 
        });
      }
    } catch (error) {
      console.error('Error checking username:', error);
    }
  };

  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value;
    
    if (!email || !email.includes('@')) {
      return;
    }
    
    try {
      const { data, error } = await supabase.rpc(
        'is_email_unique',
        { email }
      );
      
      if (error) {
        console.error('Error checking email:', error);
        return;
      }
      
      if (!data) {
        // Email exists — check if it's unconfirmed or genuinely taken
        try {
          const { data: sentAt } = await supabase.rpc('get_confirmation_sent_at', { p_email: email });
          if (sentAt === null) {
            // email_confirmed_at is set — genuinely taken
            form.setError('email', { type: 'manual', message: t('auth.emailTaken') });
          } else {
            const diffMs = new Date().getTime() - new Date(sentAt).getTime();
            const THREE_MINUTES_MS = 3 * 60 * 1000;
            if (diffMs < THREE_MINUTES_MS) {
              // Recent email — just inform user to check inbox
              form.setError('email', { type: 'manual', message: t('auth.emailNeedsConfirmation') });
            } else {
              // Expired — send a new confirmation email
              const { error: resendErr } = await supabase.auth.resend({
                type: 'signup',
                email,
                options: { emailRedirectTo: `${window.location.origin}/sign-in` }
              });
              if (!resendErr) {
                form.setError('email', { type: 'manual', message: t('auth.emailConfirmationResent') });
              } else {
                form.setError('email', { type: 'manual', message: t('auth.emailTaken') });
              }
            }
          }
        } catch {
          form.setError('email', { type: 'manual', message: t('auth.emailTaken') });
        }
      }
    } catch (error) {
      console.error('Error checking email:', error);
    }
  };

  const handleSponsorBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const sponsorCode = e.target.value;
    
    if (sponsorCode.length < 3) {
      setSponsorValid(false);
      return;
    }
    
    try {
      const { data, error } = await supabase.rpc(
        'is_referral_code_valid',
        { referral_code: sponsorCode }
      );
      
      if (error) {
        console.error('Error checking sponsor:', error);
        setSponsorValid(false);
        return;
      }
      
      if (data) {
        setSponsorValid(true);
        form.clearErrors('sponsor');
      } else {
        setSponsorValid(false);
        form.setError('sponsor', { 
          type: 'manual', 
          message: t('auth.sponsorNotFound') 
        });
      }
    } catch (error) {
      console.error('Error checking sponsor:', error);
      setSponsorValid(false);
    }
  };

  const onSubmit = async (values: SignUpFormValues) => {
    console.log('onSubmit called with values:', values);
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      console.log('Calling signup function...');
      const { success, error } = await signUp({
        username: values.username.trim(),
        firstName: values.first_name.trim(),
        lastName: values.last_name.trim(),
        email: values.email,
        password: values.password,
        dateOfBirth: values.date_of_birth,
        gender: values.gender,
        phone: values.country_code + values.phone,
        country: values.country,
        referralCode: values.sponsor,
        language: language
      });
      
      console.log('Signup result:', { success, error });
      setIsLoading(false);
      
      if (success) {
        console.log('Signup successful, navigating to verify-email page');
        navigate('/verify-email', { state: { email: values.email } });
      } else {
        console.log('Signup failed with error:', error);
        // Error is already translated in AuthContext, so we can use it directly
        setErrorMessage(error || t('auth.supabase.genericError'));
        setTimeout(() => {
          scrollToFirstError(formRef.current);
        }, 100);
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      setIsLoading(false);
      // Translate any unexpected errors
      const translatedError = translateSupabaseError(error, t);
      setErrorMessage(translatedError);
      setTimeout(() => {
        scrollToFirstError(formRef.current);
      }, 100);
    }
  };

  // If still checking authentication state, show loading
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-finance-800 mx-auto"></div>
            <p className="mt-4 text-lg text-neutral-300">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // If authenticated, show loading while useEffect handles redirect
  if (isAuthenticated) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-finance-800 mx-auto"></div>
            <p className="mt-4 text-lg text-neutral-300">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div ref={pageTopRef} className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900">
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        <div className="absolute top-0 right-1/3 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full bg-purple-500/10 blur-3xl"></div>
        
        <div className="relative z-10 container mx-auto px-4 pt-28 pb-16">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img 
                src={theme === 'dark' ? "/logo_1_inverted_no_background.png" : "/logo_1_inverted_no_background.png"} 
                alt="The Club Logo" 
                className="h-16 w-auto" 
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-2">
              {t('auth.createAccount')}
            </h1>
            <p className="text-2xl md:text-2xl text-gray-200 pt-6">
              {t('auth.createAccountMessage')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto glassmorphism-dark p-8 rounded-xl">
            {errorMessage && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-gray-400 rounded-md p-3 mb-4 text-sm">
                {errorMessage}
              </div>
            )}
            
            <Form {...form}>
              <div ref={formRef}>
                <MultiStepSignUp
                  form={form}
                  onSubmit={onSubmit}
                  isLoading={isLoading}
                  passwordRequirements={passwordRequirements}
                  onUsernameBlur={handleUsernameBlur}
                  onEmailBlur={handleEmailBlur}
                  onSponsorBlur={handleSponsorBlur}
                  sponsorValid={sponsorValid}
                />
              </div>
            </Form>
            
            <div className="text-center text-base text-neutral-300 mt-6">
              {t('auth.haveAccount')}{' '}
              <Link to={getLocalizedPath('/sign-in', language)} className="text-purple-400 hover:text-purple-300 font-medium">
                {t('auth.signIn')}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SignUp;
